import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

import { Badge, DataTable, SearchInput, Segmented, StatePanel } from "../../components/ui";

import "./email-catalog-panel.scss";

const AUDIENCES = [
  { value: "all", label: "All" },
  { value: "client", label: "Client facing" },
  { value: "admin", label: "Admin facing" },
];

// null = the backend can't count this email (plain-text alerts, Stripe receipts).
const fmtCount = (n) => (n === null || n === undefined ? "Not tracked" : n.toLocaleString());

const COLUMNS = [
  {
    key: "name",
    header: "Email",
    card: "title",
    cell: (row) => (
      <span className="email-catalog__name">
        {row.name}
        {!row.live && <Badge tone="warning" size="sm">Not sent anywhere</Badge>}
      </span>
    ),
  },
  {
    key: "audience",
    header: "Audience",
    card: "aside",
    cell: (row) => (
      <Badge tone={row.audience === "client" ? "accent" : "muted"} size="sm">
        {row.audience === "client" ? "Client" : "Admin"}
      </Badge>
    ),
  },
  { key: "trigger", header: "Sent when", card: "subtitle", className: "email-catalog__trigger" },
  { key: "channel", header: "Via" },
  { key: "last_30d", header: "Last 30 days", align: "end", cell: (row) => fmtCount(row.last_30d) },
  { key: "total", header: "Total", align: "end", cell: (row) => fmtCount(row.total) },
];

const UNCATALOGUED_COLUMNS = [
  {
    key: "key",
    header: "Logged as",
    card: "title",
    cell: (row) =>
      row.key === "mail" ? "Untyped app emails: plain-text alerts, and app emails sent before this list existed" : <code>{row.key}</code>,
  },
  { key: "last_30d", header: "Last 30 days", align: "end", cell: (row) => fmtCount(row.last_30d) },
  { key: "total", header: "Total", align: "end", cell: (row) => fmtCount(row.total) },
];

/**
 * Retail Circle Admin → Emails: every e-mail the platform can send, grouped,
 * with who gets it, what triggers it and how many went out.
 * GET /api/admin/emails (backend App\Constants\EmailCatalog + email_sends).
 */
export function EmailCatalogPanel() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [audience, setAudience] = useState("all");
  const [query, setQuery] = useState("");

  useEffect(() => {
    let alive = true;
    axios
      .get("/api/admin/emails")
      .then((res) => alive && setData(res.data))
      .catch(() => alive && setError(true));
    return () => {
      alive = false;
    };
  }, []);

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const byGroup = new Map();
    (data?.emails || [])
      .filter((e) => audience === "all" || e.audience === audience)
      .filter((e) => !q || `${e.name} ${e.trigger} ${e.key}`.toLowerCase().includes(q))
      .forEach((e) => {
        if (!byGroup.has(e.group)) byGroup.set(e.group, []);
        byGroup.get(e.group).push(e);
      });
    return [...byGroup.entries()];
  }, [data, audience, query]);

  if (error) return <StatePanel state="error" title="Could not load the email list." />;
  if (!data) return <StatePanel state="loading" />;

  const since = data.tracking_since ? new Date(data.tracking_since).toLocaleDateString() : null;

  return (
    <div className="email-catalog">
      <p className="email-catalog__intro">
        Every email the platform can send. Counts are recipients (one email to three people counts three)
        {since ? `, tracked since ${since}` : ", nothing tracked yet"}.
      </p>

      <div className="email-catalog__filters">
        <Segmented label="Audience" options={AUDIENCES} value={audience} onChange={setAudience} size="sm" />
        <SearchInput
          size="sm"
          placeholder="Search emails"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onClear={() => setQuery("")}
        />
      </div>

      {groups.length === 0 && <StatePanel state="no-results" />}

      {groups.map(([group, rows]) => (
        <section key={group} className="email-catalog__group">
          <h3 className="email-catalog__group-title">
            {group} <span className="email-catalog__group-count">{rows.length}</span>
          </h3>
          <DataTable caption={`${group} emails`} columns={COLUMNS} rows={rows} rowKey="key" />
        </section>
      ))}

      {data.uncatalogued?.length > 0 && (
        <section className="email-catalog__group">
          <h3 className="email-catalog__group-title">Logged but not in this list</h3>
          <DataTable caption="Logged emails not in the list" columns={UNCATALOGUED_COLUMNS} rows={data.uncatalogued} rowKey="key" />
        </section>
      )}
    </div>
  );
}
